// apps/client/src/pages/StorePage.tsx
//
// Store page — V2 Beelucky Sweepstakes
// Timed power-ups with real value: Auto-Dauber (3 days), x2 Multiplier (3 hours)
//

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUserStore } from '../store/userStore';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui-kit/Button';
import Icon from '../ui-kit/Icon';
import { BottomNav } from '../ui-kit/Layouts';
import { api } from '../lib/api';

type ViewType = 'home' | 'game' | 'missions' | 'store' | 'referrals' | 'sunday' | 'profile';

interface StorePageProps {
    onNavigate: (view: ViewType) => void;
    defaultTab?: 'powerups' | 'ofertas';
}

type StoreTab = 'powerups' | 'ofertas';

interface StoreItem {
    id: string;
    name: string;
    description: string;
    icon: string;
    price: number;
    durationMinutes: number;
    available: boolean;
}

interface ActivePowerUp {
    id: string;
    itemId: string;
    itemName: string;
    expiresAt: string;
    remainingMinutes: number;
}

const STORE_ITEMS: StoreItem[] = [
    {
        id: 'x2-multiplier',
        name: 'x2 Multiplier',
        description: 'Watch 1 ad → get 2 daily cards for 3 hours',
        icon: 'looks_two',
        price: 750,
        durationMinutes: 180,
        available: true,
    },
];

function formatDuration(minutes: number, t: any): string {
    if (minutes >= 1440) return t('store.days', { d: Math.floor(minutes / 1440) });
    if (minutes >= 60) return t('store.hours', { h: Math.floor(minutes / 60) });
    return t('store.mins', { m: minutes });
}

function formatRemaining(minutes: number, t: any): string {
    if (minutes <= 0) return t('store.expired');
    if (minutes >= 1440) {
        const days = Math.floor(minutes / 1440);
        const hrs = Math.floor((minutes % 1440) / 60);
        return t('store.daysHoursLeft', { days, hrs });
    }
    if (minutes >= 60) {
        const hrs = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return t('store.hoursMinsLeft', { hrs, mins });
    }
    return t('store.minsLeft', { mins: minutes });
}

export function StorePage({ onNavigate, defaultTab = 'powerups' }: StorePageProps) {
    const { t } = useTranslation();
    const { chips, watchAd, adWatchingPlacement, refreshBalance } = useUserStore();
    const [activeTab, setActiveTab] = useState<StoreTab>(defaultTab);
    const [showSuccess, setShowSuccess] = useState<string | null>(null);
    const [activePowerUps, setActivePowerUps] = useState<ActivePowerUp[]>([]);
    const [buying, setBuying] = useState<string | null>(null);
    const [loadingPowerUps, setLoadingPowerUps] = useState(false);

    // Fetch active power-ups
    const fetchPowerUps = useCallback(async () => {
        try {
            setLoadingPowerUps(true);
            const res = await api.get('/store/my-powerups');
            if (res.ok) {
                setActivePowerUps(res.powerups);
            }
        } catch (err) {
            console.error('Failed to fetch power-ups:', err);
        } finally {
            setLoadingPowerUps(false);
        }
    }, []);

    useEffect(() => {
        fetchPowerUps();
    }, [fetchPowerUps]);

    // Refresh remaining time every minute
    useEffect(() => {
        const interval = setInterval(() => {
            setActivePowerUps((prev) =>
                prev.map((p) => ({
                    ...p,
                    remainingMinutes: Math.max(
                        0,
                        Math.floor((new Date(p.expiresAt).getTime() - Date.now()) / 60000),
                    ),
                })).filter((p) => p.remainingMinutes > 0),
            );
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    const handleBuyItem = async (item: StoreItem) => {
        if (buying) return;
        if (chips < item.price) return;

        // Check if already active
        const isActive = activePowerUps.some((p) => p.itemId === item.id);
        if (isActive) return;

        try {
            setBuying(item.id);
            const res = await api.post('/store/buy', { itemId: item.id });
            if (res.ok) {
                setShowSuccess(`${t('store.itemX2Name')} activated!`);
                setTimeout(() => setShowSuccess(null), 3000);
                await refreshBalance();
                await fetchPowerUps();
            }
        } catch (err: any) {
            setShowSuccess(err?.message || t('store.purchaseFailed'));
            setTimeout(() => setShowSuccess(null), 3000);
        } finally {
            setBuying(null);
        }
    };

    const handleWatchAd = async () => {
        const result = await watchAd('store_chips');
        if (result.success && result.reward) {
            setShowSuccess(`+${result.reward} BingoChips`);
            setTimeout(() => setShowSuccess(null), 3000);
        }
    };

    const handleNavAction = (tab: string) => {
        const validViews = ['game', 'store', 'sunday', 'home', 'missions', 'referrals', 'profile'] as const;
        type ValidView = typeof validViews[number];
        if (tab === 'store') return;
        if (validViews.includes(tab as ValidView)) {
            onNavigate(tab as ValidView);
        } else {
            onNavigate('home');
        }
    };

    const tabs: { id: StoreTab; label: string; icon: string }[] = [
        { id: 'powerups', label: t('store.tabPowerups'), icon: 'bolt' },
        { id: 'ofertas', label: t('store.tabOffers'), icon: 'local_offer' },
    ];

    const isItemActive = (itemId: string) =>
        activePowerUps.some((p) => p.itemId === itemId && p.remainingMinutes > 0);

    const getItemRemaining = (itemId: string) => {
        const p = activePowerUps.find((pu) => pu.itemId === itemId);
        return p ? formatRemaining(p.remainingMinutes, t) : null;
    };

    return (
        <div className="min-h-screen bg-purple-gradient">
            {/* Header */}
            <header className="px-4 pt-6 pb-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => onNavigate('home')}
                        className="w-10 h-10 rounded-full bg-white/10 border border-white/10 flex items-center justify-center"
                    >
                        <Icon name="arrow_back" className="text-white" />
                    </motion.button>
                    <div>
                        <span className="text-[10px] font-bold text-primary uppercase tracking-[0.3em]">{t('home.bingoton')}</span>
                        <h1 className="text-lg font-bold text-white">{t('store.title')}</h1>
                    </div>
                </div>
                <div className="flex items-center gap-1 bg-white/5 px-3 py-1 rounded-pill">
                    <Icon name="toll" className="text-sm text-[#FBBF24]" />
                    <span className="text-sm font-bold text-[#FBBF24]">{chips}</span>
                </div>
            </header>

            <main className="pt-4 pb-32 px-4 max-w-lg mx-auto">
                {/* Success / Error toast */}
                <AnimatePresence>
                    {showSuccess && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="mb-4 py-2 px-4 bg-green-500/20 border border-green-500/30 rounded-xl text-green-400 text-sm font-bold text-center"
                        >
                            {showSuccess}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Tab bar */}
                <div className="flex gap-1 bg-white/5 rounded-2xl p-1 mb-6">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${activeTab === tab.id
                                ? 'bg-primary text-background'
                                : 'text-white/40 hover:text-white/60'
                                }`}
                        >
                            <Icon name={tab.icon} className="text-sm" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <AnimatePresence mode="wait">
                    {activeTab === 'powerups' && (
                        <motion.div
                            key="powerups"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            className="space-y-3"
                        >
                            {STORE_ITEMS.map((item) => {
                                const active = isItemActive(item.id);
                                const remaining = getItemRemaining(item.id);

                                return (
                                    <div
                                        key={item.id}
                                        className={`glass-panel rounded-2xl p-4 flex items-center gap-4 transition-all ${active
                                            ? 'border-green-500/30 shadow-[0_0_15px_rgba(34,197,94,0.2)]'
                                            : item.available
                                                ? 'border-white/10 hover:border-white/20'
                                                : 'border-white/5 opacity-50'
                                            }`}
                                    >
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${active ? 'bg-green-500/20' : 'bg-primary/10'
                                            }`}>
                                            <Icon
                                                name={item.icon}
                                                className={`text-2xl ${active ? 'text-green-400' : 'text-primary'}`}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-white font-bold text-sm">{t('store.itemX2Name')}</h3>
                                                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-md ${active
                                                    ? 'bg-green-500/20 text-green-400'
                                                    : 'bg-primary/10 text-primary'
                                                    }`}>
                                                    {active ? t('store.active') : formatDuration(item.durationMinutes, t)}
                                                </span>
                                            </div>
                                            <p className="text-white/40 text-xs mt-0.5">
                                                {active ? remaining : t('store.itemX2Desc')}
                                            </p>
                                        </div>
                                        {active ? (
                                            <div className="flex items-center gap-1 text-green-400">
                                                <Icon name="check_circle" className="text-lg" />
                                            </div>
                                        ) : (
                                            <Button
                                                onClick={() => handleBuyItem(item)}
                                                disabled={!item.available || chips < item.price || buying === item.id}
                                                isLoading={buying === item.id}
                                                size="sm"
                                                icon="toll"
                                            >
                                                {item.available ? item.price.toString() : t('store.soon')}
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}

                            {loadingPowerUps && (
                                <div className="text-center py-4">
                                    <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto" />
                                </div>
                            )}
                        </motion.div>
                    )}

                    {activeTab === 'ofertas' && (
                        <motion.div
                            key="ofertas"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            className="space-y-4"
                        >
                            {/* Free chips via ads — compact horizontal card */}
                            <div className="relative overflow-hidden glass-panel border border-cyan-400/20 rounded-2xl p-4">
                                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-600/10 pointer-events-none" />
                                <div className="flex items-center gap-4 relative z-10">
                                    <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0 shadow-inner">
                                        <Icon name="play_circle" className="text-2xl text-cyan-300 drop-shadow-md" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-white font-bold text-sm drop-shadow-sm">{t('store.freeChips')}</h3>
                                        <p className="text-white/60 text-xs">{t('store.watchAd')}</p>
                                    </div>
                                    <Button
                                        onClick={handleWatchAd}
                                        disabled={adWatchingPlacement !== null}
                                        isLoading={adWatchingPlacement === 'store_chips'}
                                        variant="primary"
                                        size="sm"
                                    >
                                        {adWatchingPlacement === 'store_chips' ? '...' : '+50'}
                                    </Button>
                                </div>
                            </div>

                            {/* Coming Soon placeholder */}
                            <div className="glass-panel border-white/10 rounded-2xl p-6 relative overflow-hidden">
                                {/* Coming Soon badge */}
                                <div className="absolute top-3 right-3">
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#FBBF24]/20 border border-[#FBBF24]/30 rounded-full animate-pulse shadow-glow-gold">
                                        <span className="w-1.5 h-1.5 bg-[#FBBF24] rounded-full" />
                                        <span className="text-[10px] font-bold text-[#FBBF24] uppercase tracking-wider drop-shadow-sm">{t('store.comingSoon')}</span>
                                    </span>
                                </div>

                                <div className="text-center pt-2 pb-4">
                                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-purple-700/20 border border-purple-400/20 flex items-center justify-center mx-auto mb-3 shadow-inner">
                                        <Icon name="local_offer" className="text-3xl text-purple-300 drop-shadow-md" />
                                    </div>
                                    <h3 className="text-white font-bold text-base mb-1 drop-shadow-sm">{t('store.moreOffers')}</h3>
                                    <p className="text-white/60 text-xs max-w-[200px] mx-auto">
                                        {t('store.moreOffersDesc')}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>

            <BottomNav activeTab="store" onTabChange={handleNavAction} />
        </div>
    );
}
